<script setup>
  import { ref, watch, computed } from "vue";
  import { storeToRefs } from "pinia";
  import draggable from "vuedraggable";
  import { GripVertical, Plus, BarChart3, Layers2, Copy, Trash2, Funnel } from "lucide-vue-next";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
  import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";
  import { useCardsStore } from "@stores/survey/cards";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useQuestionCardTypes } from "@/utils/composables/questionCard";
  import { getConditionsOverviewText } from "@utils/composables/conditionsOverview";
  import { languageDirection } from "@/utils/textDirection";
  import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { useDecodeMessage } from "@/utils/composables/decodeMessage";
  import { revealAndScrollToCard } from "@/utils/scrollCardIntoView";
  import { firstBlockText, removeHtmlMarkdownAndLineBreak } from "@/utils/toolBox";
  import AddCardDialog from "./AddCardDialog.vue";
  import { cardSubtype, cardTypeMeta, contentSubtype } from "./constants";
  import { analysisCardSubtitle } from "./analysisSubtitle";
  import RecodeDeleteDialog from "./RecodeDeleteDialog.vue";

  const { decodeName } = useDecodeMessage();
  const store = useResultsV2Store();
  const cardsStore = useResultsV2CardsStore();
  const recodesStore = useResultsV2RecodesStore();
  const { currentView, editMode, availableCards, savedPopulations } = storeToRefs(store);
  const { cards: viewCards, selectedCard } = storeToRefs(cardsStore);

  const localesStore = useSurveyLocalesStore();
  const { analysisLocale } = storeToRefs(localesStore);

  const showAddDialog = ref(false);
  const searchQuery = ref("");
  const hoveredIconId = ref(null);

  const localCards = ref([]);

  // Sync from store → local
  watch(
    viewCards,
    (cards) => {
      localCards.value = cards ? [...cards] : [];
    },
    { immediate: true, deep: true },
  );

  // Build a lookup map: id → chart card metadata
  const chartCardsById = computed(() => {
    const map = {};
    for (const card of availableCards.value) {
      map[card.id] = card;
    }
    return map;
  });

  // Filter cards by search
  const filteredCards = computed(() => {
    if (!searchQuery.value.trim()) return localCards.value;
    const query = searchQuery.value.toLowerCase();
    return localCards.value.filter((card) => {
      const label = getCardLabel(card).toLowerCase();
      return label.includes(query);
    });
  });

  function handleDragEnd({ oldIndex, newIndex }) {
    if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
    const moved = localCards.value[newIndex];
    if (moved) cardsStore.moveCard(moved.id, newIndex);
  }

  async function handleCardClick(card) {
    cardsStore.selectCard(card.id);
    await revealAndScrollToCard(card.id);
  }

  function recodeTokenFor(card) {
    return store.recodeTokenForViewCard(card);
  }

  const recodeDeleteTarget = ref(null);
  const deletingRecode = ref(false);

  const showRecodeDeleteDialog = computed({
    get: () => !!recodeDeleteTarget.value,
    set: (open) => {
      if (!open) recodeDeleteTarget.value = null;
    },
  });

  function isProgrammingRecode(card) {
    const token = recodeTokenFor(card);
    const recodeCard = token ? useCardsStore().findByToken(token) : null;
    return !!recodeCard && recodeCard.is_for_analytics === false;
  }

  function handleDeleteCard(card) {
    if (card.card_type === "recode" && !card._isNew && recodeTokenFor(card)) {
      recodeDeleteTarget.value = card;
      return;
    }
    cardsStore.removeCard(card.id);
  }

  function removeRecodeFromDashboard() {
    if (recodeDeleteTarget.value) cardsStore.removeCard(recodeDeleteTarget.value.id);
    recodeDeleteTarget.value = null;
  }

  async function deleteRecodePermanently() {
    const token = recodeTokenFor(recodeDeleteTarget.value);
    if (!token || deletingRecode.value) return;
    deletingRecode.value = true;
    const deleted = await recodesStore.deleteRecode(token);
    deletingRecode.value = false;
    if (deleted) recodeDeleteTarget.value = null;
  }

  function canDuplicateCard(card) {
    return card.card_type === "recode" && !card._isNew && !!recodeTokenFor(card);
  }

  function handleDuplicateCard(card) {
    const token = recodeTokenFor(card);
    if (!token) return;
    const sorted = [...viewCards.value].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((c) => c.id === card.id);
    const next = idx >= 0 ? sorted[idx + 1] : null;
    const position = next ? (card.position + next.position) / 2 : card.position + 100;
    store.duplicateRecodeCard(token, position);
  }

  const insertAfterPosition = ref(null);

  function handleAddCardAfter(card) {
    insertAfterPosition.value = card?.position || null;
    showAddDialog.value = true;
  }

  const { determineIcon } = useQuestionCardTypes(ref({}));
  const textDirection = computed(() => languageDirection(store.displayLanguage));

  function getCardIcon(card) {
    if (card.card_type === "question") {
      const meta = chartCardsById.value[card.card_id];
      if (meta?.type) return determineIcon(meta.type);
      return BarChart3;
    }
    if (card.card_type === "recode") return Layers2;
    return cardTypeMeta(cardSubtype(card) || card.card_type)?.icon || BarChart3;
  }

  const isAnalysisFamily = (card) => card.card_type === "analysis";
  const isContentFamily = (card) => !!contentSubtype(card);

  function iconColorClass(card) {
    if (card.card_type === "question") return "bg-status-info-soft text-status-info-strong";
    if (card.card_type === "recode") return "bg-status-warning-soft text-status-warning-strong";
    if (isAnalysisFamily(card)) return "bg-purple-100 text-purple-600";
    if (isContentFamily(card)) return "bg-status-success-soft text-status-success-strong";
    return "bg-secondary text-foreground-secondary";
  }

  function resolveTitle(title) {
    if (!title) return "";
    let resolved;
    if (typeof title === "string") resolved = title;
    else if (typeof title === "object") {
      const localeToken = currentView.value?.options?.locale_token || analysisLocale.value?.token;
      resolved = (localeToken && title[localeToken]) || Object.values(title)[0] || "";
    } else resolved = String(title);
    return decodeName(resolved);
  }

  function getCardLabel(card) {
    const contentKind = contentSubtype(card);
    if (contentKind === "section") {
      return firstBlockText(card.content?.title) || cardTypeMeta("section").label;
    }
    if (contentKind === "text") {
      return firstBlockText(card.content?.text) || cardTypeMeta("text").label;
    }
    if (contentKind === "image") {
      return card.content?.caption || cardTypeMeta("image").label;
    }
    const customName = card.options?.custom_name_title?.name;
    if (customName) return customName;
    const subtype = cardSubtype(card);
    if (subtype) return cardTypeMeta(subtype)?.label || card.card_type;
    // For new recodes being configured, show the config name
    if (card._recodeToken) {
      const config = recodesStore.recodeConfigs[card._recodeToken];
      if (config?.name) return config.name;
      return "New recode";
    }
    // Card name as primary label (like the Questions editing page)
    const meta = chartCardsById.value[card.card_id];
    if (meta) return decodeName(meta.name || meta.token);
    return card.card_type;
  }

  function getCardConditions(card) {
    if (card.card_type !== "question") return null;
    const conditions = chartCardsById.value[card.card_id]?.specs?.conditions;
    return conditions?.length ? conditions : null;
  }

  function getCardSubtitle(card) {
    if (isContentFamily(card)) return "";
    const customTitle = card.options?.custom_name_title?.title;
    if (customTitle) return customTitle;
    if (cardSubtype(card)) return analysisCardSubtitle(card, availableCards.value, savedPopulations.value);
    const meta = chartCardsById.value[card.card_id];
    if (meta?.specs?.question) {
      return resolveTitle(meta.specs.question);
    }
    return "";
  }
</script>

<template>
  <Card
    data-results-sidebar
    class="flex flex-col !gap-0 !py-0 h-full overflow-hidden shadow-xs">
    <CardHeader class="flex-shrink-0 px-3 pt-3 pb-2">
      <SearchInput
        v-model="searchQuery"
        variant="muted"
        placeholder="Search cards…"
        full-width />
    </CardHeader>

    <CardContent class="flex-1 flex flex-col min-h-0 px-1 pb-0">
      <div class="flex-1 overflow-y-auto px-1 py-1 [scrollbar-gutter:stable]">
        <draggable
          v-model="localCards"
          item-key="id"
          handle=".card-drag-handle"
          ghost-class="opacity-50"
          :disabled="!editMode"
          class="space-y-0.5"
          @end="handleDragEnd">
          <template #item="{ element }">
            <div
              v-show="!searchQuery || filteredCards.includes(element)"
              :id="`sidebar-card-${element.id}`"
              class="group flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-muted"
              :class="{ 'shadow-[inset_0_0_0_1px_var(--ring)]': selectedCard?.id === element.id }"
              :title="removeHtmlMarkdownAndLineBreak(getCardSubtitle(element))"
              @click="handleCardClick(element)">
              <!-- Drag handle + icon (grip appears on icon hover) -->
              <div
                class="flex items-center"
                @mouseenter="hoveredIconId = element.id"
                @mouseleave="hoveredIconId = null">
                <div
                  v-show="editMode && hoveredIconId === element.id"
                  class="mr-1 w-4 shrink-0"
                  @click.stop>
                  <GripVertical
                    class="card-drag-handle h-4 w-4 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" />
                </div>
                <div
                  class="flex size-6 shrink-0 items-center justify-center rounded-md transition-colors"
                  :class="iconColorClass(element)">
                  <component
                    :is="getCardIcon(element)"
                    class="size-3.5"
                    :stroke-width="1.8" />
                </div>
              </div>
              <!-- Label -->
              <div class="flex min-w-0 flex-1 items-baseline gap-2">
                <span
                  :dir="textDirection"
                  class="max-w-full shrink-0 truncate text-13 font-medium text-primary"
                  >{{ getCardLabel(element) }}</span
                >
                <span
                  v-if="getCardSubtitle(element)"
                  :dir="textDirection"
                  class="min-w-0 flex-1 truncate text-xs text-foreground-subtle"
                  >{{ removeHtmlMarkdownAndLineBreak(getCardSubtitle(element)) }}</span
                >
              </div>
              <!-- Programming display-logic indicator (informative only) -->
              <Tooltip
                v-if="getCardConditions(element)"
                :content="getConditionsOverviewText(getCardConditions(element))">
                <span class="shrink-0 pl-1 text-muted-foreground">
                  <Funnel class="size-3.5" />
                </span>
              </Tooltip>
              <!-- Actions (edit mode, visible on hover, no reserved width) -->
              <div
                v-if="editMode"
                class="flex items-center gap-0.5 shrink-0 w-0 overflow-hidden group-hover:w-auto group-hover:overflow-visible group-hover:pl-1.5 transition-all duration-200"
                @click.stop>
                <Tooltip content="Add cards">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="size-6 text-muted-foreground"
                    @click="handleAddCardAfter(element)">
                    <Plus class="size-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip
                  v-if="canDuplicateCard(element)"
                  content="Duplicate">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="size-6 text-muted-foreground"
                    @click="handleDuplicateCard(element)">
                    <Copy class="size-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip content="Delete">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="size-6 text-muted-foreground"
                    @click="handleDeleteCard(element)">
                    <Trash2 class="size-3.5" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </template>
        </draggable>
        <EmptyState
          v-if="!localCards.length"
          title="No cards yet"
          size="sm"
          class="px-4" />
      </div>
    </CardContent>

    <CardFooter
      v-if="editMode"
      class="py-2 px-3 border-t bg-background shrink-0 flex items-center justify-between">
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5 shadow-xs"
        @click="
          insertAfterPosition = null;
          showAddDialog = true;
        ">
        <Plus class="h-4 w-4" />
        Add cards
      </Button>
    </CardFooter>

    <AddCardDialog
      v-model:open="showAddDialog"
      :insert-position="insertAfterPosition" />

    <RecodeDeleteDialog
      v-model:open="showRecodeDeleteDialog"
      :deleting="deletingRecode"
      :allow-delete="recodeDeleteTarget ? !isProgrammingRecode(recodeDeleteTarget) : true"
      @remove="removeRecodeFromDashboard"
      @delete="deleteRecodePermanently" />
  </Card>
</template>
